import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUploadDocument } from '../../../hooks/useDocs';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from '../constants';
import { APP_PAGES } from '../../../pages';
import { toast } from '../../../Utils/toast';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { upload, isUploading, progress, error, success, uploadedDoc, reset } = useUploadDocument();

  const validateFile = (file: File): string | null => {
    // Check file extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(extension as any)) {
      return `File type not supported. Please upload: ${ALLOWED_FILE_TYPES.join(', ')}`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`;
    }

    // Check MIME type (optional, as browsers may not always report correctly)
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      // Warning but don't block - MIME types can be unreliable
      console.warn(`Unexpected MIME type: ${file.type}`);
    }

    return null;
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setFile(selectedFile);
    reset();
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    []
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      await upload(file);
      toast.success(`Document "${file.name}" uploaded successfully`);
      setTimeout(() => {
        navigate(APP_PAGES.DOCS_LIBRARY);
      }, 1500);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleRemove = () => {
    setFile(null);
    reset();
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Typography variant="h1" sx={{ mb: 4 }}>
          Upload Document
        </Typography>

        <Paper sx={{ p: 4 }}>
          {/* Drop Zone */}
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${dragActive ? '#0B93DF' : '#DEDEED'}`,
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              bgcolor: dragActive ? '#F0F8FF' : '#FAFAFA',
              cursor: 'pointer',
              transition: 'all 0.3s',
              mb: 3,
            }}
            onClick={() => document.getElementById('file-input')?.click()}
            data-testid="docs-upload-dropzone"
          >
            <CloudUploadIcon sx={{ fontSize: 64, color: '#0B93DF', mb: 2 }} />
            <Typography variant="h4" sx={{ mb: 1 }}>
              Drag & drop a file here
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported: {ALLOWED_FILE_TYPES.join(', ')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Max size: {MAX_FILE_SIZE_MB}MB
            </Typography>
            <input
              id="file-input"
              type="file"
              accept={ALLOWED_FILE_TYPES.join(',')}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              aria-label="File input"
            />
          </Box>

          {/* Selected File */}
          {file && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#F8F8F8' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={handleRemove}
                  disabled={isUploading}
                  data-testid="upload-remove-button"
                >
                  Remove
                </Button>
              </Box>
            </Paper>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Uploading... {progress}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Success */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Upload successful! Redirecting to library...
            </Alert>
          )}

          {/* Actions */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(APP_PAGES.DOCS_LIBRARY)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!file || isUploading}
              data-testid="docs-upload-submit-button"
            >
              Upload Document
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Upload;
